import { yupResolver } from '@hookform/resolvers/yup'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'

import { HIDE_ON_MOBILE_INLINE } from '@/theme/responsive'

import {
  compactDialogContentSx,
  dialogHeaderBtnSx,
  dialogTitleActionsSx,
  dialogTitleSx,
  dialogTitleTextSx,
  numSx,
  roSx,
} from './dialogStyle'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { usePagePermission } from '@/hooks/usePagePermission'
import { useSnackbar } from '@/hooks/useSnackbar'
import { useGetLocalPQuery, useListLocalPQuery } from '@/store/api/localPApi'
import {
  useCreateForwardMutation,
  useListForwardsQuery,
  useUpdateForwardMutation,
} from '@/store/api/localPForwardsApi'
import {
  useListForwardTypesQuery,
  useListPriceUnitsQuery,
} from '@/store/api/lookupsApi'
import type {
  ForwardType,
  LocalPForwardCreateIn,
  LocalPForwardOut,
  LocalPVer2Item,
} from '@/types/api'
import { formatDate } from '@/utils/date'
import { getErrorMessage } from '@/utils/error'
import { formatNumber } from '@/utils/money'

export interface DepositFormDialogProps {
  open: boolean
  mode: 'create' | 'edit'
  fixedContractNo?: string
  /** Selected row from /purchase/contract/forward-pu — provides
   *  `to_be_received`, `deposit_value_per`, `total_repayment` used in
   *  Access `check_suggest` for auto-fill. */
  ver2Row?: LocalPVer2Item | null
  initialValue?: LocalPForwardOut | null
  showDetailList?: boolean
  onClose: () => void
  onSaved?: (forwardId: number) => void
}

interface DepositFormValues {
  contract_no: string
  forward_type: ForwardType | ''
  payment_date: string
  price: number | ''
  market_closing_price: number | ''
  fx: number | ''
  quantity: number | ''
  amount: number | ''
  price_unit: string
  note: string
}

type DepositDetailColumn = {
  key: keyof LocalPForwardOut
  label: string
  kind?: 'date' | 'datetime' | 'number'
}

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const DETAIL_COLUMNS: DepositDetailColumn[] = [
  { key: 'supplier_code', label: 'Supplier Code' },
  { key: 'name', label: 'Supplier Name' },
  { key: 'contract_no', label: 'Contract No' },
  { key: 'forward_type', label: 'Deposit Type' },
  { key: 'payment_date', label: 'Payment Date', kind: 'date' },
  { key: 'price', label: 'Price', kind: 'number' },
  { key: 'market_closing_price', label: 'Market Closing Price', kind: 'number' },
  { key: 'fx', label: 'FX', kind: 'number' },
  { key: 'quantity', label: 'Quantity', kind: 'number' },
  { key: 'amount', label: 'Amount', kind: 'number' },
  { key: 'created_date', label: 'Created Date' },
  { key: 'updated_date', label: 'Updated Date', kind: 'datetime' },
  { key: 'created_by', label: 'Created By' },
  { key: 'updated_by', label: 'Updated By' },
  { key: 'note', label: 'Note' },
  { key: 'id', label: 'ID', kind: 'number' },
  { key: 'deposit_repayment_date', label: 'Repayment Date', kind: 'date' },
  { key: 'interest_value', label: 'Interest Value', kind: 'number' },
  { key: 'price_unit', label: 'Price Unit' },
]

const createEmptyValues = (contractNo?: string): DepositFormValues => ({
  contract_no: contractNo ?? '',
  forward_type: '',
  payment_date: dayjs().format('YYYY-MM-DD'),
  price: '',
  market_closing_price: '',
  fx: '',
  quantity: '',
  amount: '',
  price_unit: '',
  note: '',
})

const schema: yup.ObjectSchema<DepositFormValues> = yup.object({
  contract_no: yup.string().trim().required('Contract No is required'),
  forward_type: yup
    .mixed<ForwardType | ''>()
    .oneOf(['First deposit', 'Top up', 'Repayment', ''] as const)
    .test('required', 'Deposit Type is required', (value) => value !== '' && !!value)
    .required(),
  payment_date: yup
    .string()
    .trim()
    .test('required', 'Payment Date is required', (value) => !!value && value.trim() !== '')
    .default(''),
  price: yup.mixed<number | ''>().default(''),
  market_closing_price: yup.mixed<number | ''>().default(''),
  fx: yup.mixed<number | ''>().default(''),
  quantity: yup.mixed<number | ''>().default(''),
  amount: yup.mixed<number | ''>().default(''),
  price_unit: yup.string().trim().default(''),
  note: yup.string().trim().default(''),
}) as yup.ObjectSchema<DepositFormValues>

const formatAccessDate = (value: unknown): string => {
  if (!value) return ''
  const date = new Date(String(value))
  if (Number.isNaN(date.getTime())) return String(value)
  return `${`${date.getDate()}`.padStart(2, '0')}-${ACCESS_MONTHS[date.getMonth()]}-${`${date.getFullYear()}`}`
}

const formatAccessDateTime = (value: unknown): string => {
  if (!value) return ''
  const date = new Date(String(value))
  if (Number.isNaN(date.getTime())) return String(value)
  const day = `${date.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[date.getMonth()]
  const year = `${date.getFullYear()}`
  const hh = `${date.getHours()}`.padStart(2, '0')
  const mm = `${date.getMinutes()}`.padStart(2, '0')
  return `${day}-${month}-${year} ${hh}:${mm}`
}

const formatAccessNumber = (value: number): string =>
  Number.isInteger(value)
    ? value.toLocaleString('en-US')
    : value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const formatDetailValue = (row: LocalPForwardOut, column: DepositDetailColumn): string => {
  const value = row[column.key]
  if (value == null) return ''
  if (column.kind === 'date') return formatAccessDate(value)
  if (column.kind === 'datetime') return formatAccessDateTime(value)
  if (column.kind === 'number' && typeof value === 'number') return formatAccessNumber(value)
  if (typeof value === 'number') return formatAccessNumber(value)
  return String(value)
}

function fromForward(forward: LocalPForwardOut): DepositFormValues {
  const num = (value: number | null | undefined): number | '' =>
    value === null || value === undefined ? '' : value
  const str = (value: string | null | undefined): string => value ?? ''
  return {
    contract_no: str(forward.contract_no),
    forward_type: (forward.forward_type as ForwardType) ?? '',
    payment_date: str(forward.payment_date),
    price: num(forward.price),
    market_closing_price: num(forward.market_closing_price),
    fx: num(forward.fx),
    quantity: num(forward.quantity),
    amount: num(forward.amount),
    price_unit: str(forward.price_unit),
    note: str(forward.note),
  }
}

/**
 * Mirror Access `frmPurchaseQryLocalPAdd_ForwardDetail.check_suggest` —
 * applies auto-suggestions based on contract_type × forward_type.
 *
 * Limitations: backend doesn't expose Vietinbank daily FX, quality g2 price,
 * or futures market close — so for those cases (Outright Top up,
 * Differential First deposit / Top up) we leave `price` / `market_closing_price`
 * at 0 and let the user fill manually.
 */
interface SuggestionContext {
  contractType?: string | null
  contractPriceUnit?: string | null
  contractOutrightPrice?: number | null
  futuresMonth?: string | null
  toBeReceived?: number | null
  depositValuePer?: number | null
  totalRepayment?: number | null
}

function suggestDepositValues(
  current: DepositFormValues,
  ctx: SuggestionContext,
): DepositFormValues {
  const ft = current.forward_type
  const ct = (ctx.contractType ?? '').toString()
  const futuresMonth = ctx.contractPriceUnit ?? ''
  const next: DepositFormValues = {
    ...current,
    quantity: ctx.toBeReceived != null ? ctx.toBeReceived : current.quantity,
  }

  // Price / market_closing_price / price_unit per (contract_type × forward_type).
  if (ct === 'Outright') {
    if (ft === 'First deposit') {
      next.price = ctx.contractOutrightPrice ?? 0
      next.market_closing_price = 0
      next.price_unit = ctx.contractPriceUnit ?? ''
    } else if (ft === 'Top up') {
      // Access: price = DLookup(price_from_g2, v_changecost_quality, code+date)
      // Backend chưa expose lookup này → để user nhập tay.
      next.price = current.price === '' ? 0 : current.price
      next.market_closing_price = 0
      next.price_unit = 'VND/KG'
    } else if (ft === 'Repayment') {
      next.price = 0
      next.market_closing_price = 0
      next.price_unit = ctx.contractPriceUnit ?? ''
    } else {
      next.price = 0
      next.market_closing_price = 0
      next.price_unit = ctx.contractPriceUnit ?? ''
    }
  } else if (ct === 'Differential') {
    if (ft === 'First deposit' || ft === 'Top up') {
      // Access: market_closing_price = market close cho futures_month tại payment_date.
      // Backend chưa expose → để 0, user nhập tay.
      next.price = 0
      // futures KCE → CTS/LB, các sàn khác → USD/MT.
      const fm = ctx.futuresMonth ?? ''
      next.price_unit = fm.toUpperCase().includes('KCE') ? 'CTS/LB' : 'USD/MT'
    } else if (ft === 'Repayment') {
      next.price = 0
      next.market_closing_price = 0
      next.price_unit = ctx.contractPriceUnit ?? ''
    } else {
      next.price = 0
      next.market_closing_price = 0
      next.price_unit = ctx.contractPriceUnit ?? ''
    }
    // Silence unused warning when not Differential
    void futuresMonth
  }

  // Amount per Access logic.
  const qty = typeof next.quantity === 'number' ? next.quantity : 0
  const priceN = typeof next.price === 'number' ? next.price : 0
  const marketN =
    typeof next.market_closing_price === 'number' ? next.market_closing_price : 0
  const depPer = ctx.depositValuePer ?? 0
  if (ft === 'First deposit' || ft === 'Top up') {
    if (ct === 'Outright') {
      next.amount = qty > 0 && priceN > 0 && depPer > 0 ? Number((qty * priceN * depPer).toFixed(0)) : 0
    } else if (ct === 'Differential') {
      next.amount =
        qty > 0 && marketN > 0 && depPer > 0 ? Number((qty * marketN * depPer).toFixed(0)) : 0
    } else {
      next.amount = 0
    }
  } else if (ft === 'Repayment') {
    next.amount = ctx.totalRepayment ?? 0
  } else {
    next.amount = 0
  }

  return next
}

function toApi(values: DepositFormValues): LocalPForwardCreateIn {
  const num = (value: number | ''): number | null => (value === '' ? null : value)
  const str = (value: string): string | null => (value.trim() === '' ? null : value.trim())
  const dateValue = values.payment_date.trim()
  return {
    contract_no: values.contract_no.trim(),
    forward_type: values.forward_type as ForwardType,
    payment_date: dateValue === '' ? null : dateValue,
    price: num(values.price),
    market_closing_price: num(values.market_closing_price),
    fx: num(values.fx),
    quantity: num(values.quantity),
    amount: num(values.amount),
    price_unit: str(values.price_unit),
    note: str(values.note),
  }
}

const numberValueOrEmpty = (raw: string): number | '' => {
  if (raw === '') return ''
  const n = Number(raw)
  return Number.isFinite(n) ? n : ''
}

export function DepositFormDialog({
  open,
  mode,
  fixedContractNo,
  ver2Row,
  initialValue,
  showDetailList = false,
  onClose,
  onSaved,
}: DepositFormDialogProps) {
  const snack = useSnackbar()
  const { canEdit } = usePagePermission()

  // Internal editing state — controls Save semantics independently from the
  // initial `mode` prop. Double-clicking a row in the detail list flips this
  // to that row; saving clears it back so the form returns to "new" mode and
  // the dialog stays open for the next entry.
  const [currentEditing, setCurrentEditing] = useState<LocalPForwardOut | null>(
    mode === 'edit' ? initialValue ?? null : null,
  )
  const isEdit = currentEditing !== null

  const [createForward, { isLoading: creating }] = useCreateForwardMutation()
  const [updateForward, { isLoading: updating }] = useUpdateForwardMutation()
  const loading = creating || updating

  const { data: forwardTypes = [] } = useListForwardTypesQuery()
  const { data: priceUnits = [] } = useListPriceUnitsQuery()
  const { data: contractsList } = useListLocalPQuery({ page: 1, page_size: 1_000_000 })
  const contractOptions = useMemo(() => contractsList?.items ?? [], [contractsList])

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<DepositFormValues>({
    resolver: yupResolver(schema),
    defaultValues: createEmptyValues(fixedContractNo),
  })

  const selectedContractNo = watch('contract_no')
  const forwardType = watch('forward_type')
  const currentContractNo = fixedContractNo ?? selectedContractNo

  const { data: contractDetailRes } = useGetLocalPQuery(currentContractNo, {
    skip: !currentContractNo,
  })
  const contractDetail = contractDetailRes?.data

  const {
    data: detailList,
    isLoading: detailListLoading,
    isFetching: detailListFetching,
    error: detailListError,
  } = useListForwardsQuery(
    { contract_no: currentContractNo, page: 1, page_size: 1_000_000 },
    { skip: !showDetailList || !currentContractNo },
  )

  const detailRows = useMemo(() => detailList?.items ?? [], [detailList])

  const resetToNew = () => {
    setCurrentEditing(null)
    reset(createEmptyValues(fixedContractNo))
  }

  const loadRowForEdit = (row: LocalPForwardOut) => {
    setCurrentEditing(row)
    reset(fromForward(row))
  }

  // Init / re-init when dialog opens. Honors `mode='edit'` + `initialValue`
  // from parent; otherwise starts fresh.
  useEffect(() => {
    if (!open) return
    if (mode === 'edit' && initialValue) {
      setCurrentEditing(initialValue)
      reset(fromForward(initialValue))
    } else {
      setCurrentEditing(null)
      reset(createEmptyValues(fixedContractNo))
    }
  }, [open, mode, initialValue, fixedContractNo, reset])

  // Access `check_suggest`: re-fire on contract_no, forward_type, payment_date.
  // Edit mode keeps the saved values — only auto-suggest in create mode.
  const suggestionCtx = useMemo<SuggestionContext>(
    () => ({
      contractType: contractDetail?.contract_type ?? ver2Row?.contract_type ?? null,
      contractPriceUnit: contractDetail?.price_unit ?? ver2Row?.price_unit ?? null,
      contractOutrightPrice: contractDetail?.outright_price ?? ver2Row?.outright_price ?? null,
      futuresMonth: contractDetail?.futures_month ?? ver2Row?.futures_month ?? null,
      toBeReceived: ver2Row?.to_be_received ?? null,
      depositValuePer: ver2Row?.deposit_value_per ?? null,
      totalRepayment: ver2Row?.total_repayment ?? null,
    }),
    [contractDetail, ver2Row],
  )

  useEffect(() => {
    if (isEdit) return
    if (!selectedContractNo) return
    // contractDetail may still be loading — but only the contract-aware
    // suggestions wait for it; we can still apply when ver2Row is present
    // since it carries the deposit aggregates.
    if (!contractDetail && !ver2Row) return

    const current: DepositFormValues = {
      contract_no: selectedContractNo,
      forward_type: forwardType as ForwardType | '',
      payment_date: watch('payment_date'),
      price: watch('price'),
      market_closing_price: watch('market_closing_price'),
      fx: watch('fx'),
      quantity: watch('quantity'),
      amount: watch('amount'),
      price_unit: watch('price_unit'),
      note: watch('note'),
    }
    const next = suggestDepositValues(current, suggestionCtx)
    if (next.quantity !== current.quantity) setValue('quantity', next.quantity)
    if (next.price !== current.price) setValue('price', next.price)
    if (next.market_closing_price !== current.market_closing_price)
      setValue('market_closing_price', next.market_closing_price)
    if (next.price_unit !== current.price_unit) setValue('price_unit', next.price_unit)
    if (next.amount !== current.amount) setValue('amount', next.amount)
  }, [
    isEdit,
    selectedContractNo,
    forwardType,
    contractDetail,
    ver2Row,
    suggestionCtx,
    setValue,
    watch,
  ])

  const interestRatePct = useMemo(() => {
    const value = contractDetail?.margin_value_per
    return typeof value === 'number' ? value * 100 : null
  }, [contractDetail])

  const onSubmit = handleSubmit(
    async (values) => {
      const payload = toApi(values)
      const editingTarget = currentEditing
      try {
        if (editingTarget) {
          const response = await updateForward({
            forwardId: editingTarget.id,
            body: payload,
          }).unwrap()
          snack.success('Deposit updated')
          onSaved?.(response.data?.id ?? editingTarget.id)
        } else {
          const response = await createForward(payload).unwrap()
          snack.success('Deposit added')
          onSaved?.(response.data?.id ?? 0)
        }
        // Reload-only behavior: list auto-refetches via RTK invalidation.
        // Reset form back to "new" so the user can add another row without
        // closing the dialog. Use Cancel to close.
        resetToNew()
      } catch (err) {
        snack.error(
          getErrorMessage(
            err,
            editingTarget ? 'Failed to update deposit' : 'Failed to add deposit',
          ),
        )
      }
    },
    () => {
      snack.error('Please review the highlighted fields.')
    },
  )

  return (
    <Dialog open={open} onClose={onClose} maxWidth={showDetailList ? 'xl' : 'md'} fullWidth scroll="paper">
      <DialogTitle sx={dialogTitleSx}>
        <Typography component="span" noWrap title="P Contract - Deposit Details" sx={dialogTitleTextSx}>
          <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>P Contract - </Box>Deposit Details
          {currentEditing ? (
            <Typography
              component="span"
              variant="caption"
              sx={{ ml: 1, color: 'warning.main', fontWeight: 600 }}
            >
              Editing #{currentEditing.id}
            </Typography>
          ) : (
            <Typography
              component="span"
              variant="caption"
              sx={{ ml: 1, color: 'text.secondary' }}
            >
              New entry
            </Typography>
          )}
        </Typography>
        <Stack direction="row" spacing={0.5} sx={dialogTitleActionsSx}>
          <Button
            variant="contained"
            size="small"
            color="success"
            onClick={() => void onSubmit()}
            disabled={loading || !canEdit}
            sx={dialogHeaderBtnSx}
          >
            {loading ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button variant="outlined" size="small" onClick={resetToNew} disabled={loading} sx={dialogHeaderBtnSx}>
            Clean
          </Button>
          <Button variant="outlined" size="small" onClick={onClose} disabled={loading} sx={dialogHeaderBtnSx}>
            Cancel
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={compactDialogContentSx}>
        <Grid container spacing={1.5} sx={{ mt: 0 }}>
          {/* Row 1: Contract No (6) | Payment Date (3) | empty (3) */}
          <Grid item xs={12} sm={6}>
            {fixedContractNo ? (
              <TextField
                label="Contract No"
                size="small"
                fullWidth
                value={fixedContractNo}
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                sx={roSx}
              />
            ) : (
              <Controller
                control={control}
                name="contract_no"
                render={({ field }) => {
                  const found = contractOptions.find((contract) => contract.contract_no === field.value)
                  const selected =
                    found ??
                    (field.value
                      ? ({
                          contract_no: field.value,
                          supplier_name: null,
                          supplier_code: null,
                        } as (typeof contractOptions)[number])
                      : null)
                  // inject sel vào options → tắt MUI "none of options match" (anti-pattern #46)
                  const contractOpts =
                    selected && !contractOptions.some((o) => o.contract_no === selected.contract_no)
                      ? [selected, ...contractOptions]
                      : contractOptions
                  return (
                    <Autocomplete
                      size="small"
                      options={contractOpts}
                      value={selected}
                      onChange={(_event, value) => field.onChange(value?.contract_no ?? '')}
                      getOptionLabel={(option) =>
                        option ? `${option.contract_no} — ${option.supplier_name ?? option.supplier_code ?? ''}` : ''
                      }
                      isOptionEqualToValue={(option, value) => option.contract_no === value.contract_no}
                      disabled={isEdit}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Contract No *"
                          error={!!errors.contract_no}
                          helperText={errors.contract_no?.message}
                          sx={isEdit ? roSx : undefined}
                        />
                      )}
                    />
                  )
                }}
              />
            )}
          </Grid>
          <Grid item xs={12} sm={3}>
            <Controller
              control={control}
              name="payment_date"
              render={({ field }) => (
                <DatePicker
                  label="Payment Date"
                  format="DD-MMM-YYYY"
                  value={field.value ? dayjs(field.value) : null}
                  onChange={(value) => field.onChange(value && value.isValid() ? value.format('YYYY-MM-DD') : '')}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      error: !!errors.payment_date,
                      helperText: errors.payment_date?.message,
                    },
                  }}
                />
              )}
            />
          </Grid>

          {/* Row 2: Contract Date (3) | Repayment Date (3) | Interest Rate (3) | Supplier Name (3) */}
          <Grid item xs={12} sm={3}>
            <TextField
              label="Contract Date"
              size="small"
              fullWidth
              value={formatDate(contractDetail?.contract_date) || ''}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              sx={roSx}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Deposit Repayment Date"
              size="small"
              fullWidth
              value={formatDate(contractDetail?.deposit_repayment_date) || ''}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              sx={roSx}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Interest Rate (%)"
              size="small"
              fullWidth
              value={interestRatePct === null ? '' : formatNumber(interestRatePct, 2)}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              sx={[numSx, roSx]}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Supplier Name"
              size="small"
              fullWidth
              value={contractDetail?.supplier_name ?? contractDetail?.short_name ?? ''}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              sx={roSx}
            />
          </Grid>

          {/* Row 3: Deposit Type (6) | Price (3) | Market Closing Price (3) */}
          <Grid item xs={12} sm={6}>
            <Controller
              control={control}
              name="forward_type"
              render={({ field }) => {
                const sel = forwardTypes.find((t) => t.value === field.value)
                  ?? (field.value ? { value: field.value, label: '' } : null)
                return (
                  <Autocomplete
                    size="small"
                    fullWidth
                    options={forwardTypes}
                    value={sel}
                    onChange={(_e, v) => field.onChange(v?.value ?? '')}
                    getOptionLabel={(o) => o ? (o.value === o.label ? o.value : `${o.value} — ${o.label ?? ''}`) : ''}
                    isOptionEqualToValue={(a, b) => a.value === b.value}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Deposit Type *"
                        error={!!errors.forward_type}
                        helperText={errors.forward_type?.message}
                      />
                    )}
                  />
                )
              }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Controller
              control={control}
              name="price"
              render={({ field }) => (
                <TextField
                  label="Price"
                  type="number"
                  size="small"
                  fullWidth
                  value={field.value === '' ? '' : field.value}
                  onChange={(event) =>
                    field.onChange(event.target.value === '' ? '' : Number(event.target.value))
                  }
                  onBlur={field.onBlur}
                  inputProps={{ inputMode: 'decimal' }}
                  error={!!errors.price}
                  helperText={errors.price?.message}
                  sx={numSx}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Controller
              control={control}
              name="market_closing_price"
              render={({ field }) => (
                <TextField
                  label="Market Closing Price"
                  type="number"
                  size="small"
                  fullWidth
                  value={field.value === '' ? '' : field.value}
                  onChange={(event) =>
                    field.onChange(event.target.value === '' ? '' : Number(event.target.value))
                  }
                  onBlur={field.onBlur}
                  inputProps={{ inputMode: 'decimal' }}
                  error={!!errors.market_closing_price}
                  helperText={errors.market_closing_price?.message}
                  sx={numSx}
                />
              )}
            />
          </Grid>

          {/* Row 4: FX (3) | Quantity (3) | Price Unit (3) | empty (3) */}
          <Grid item xs={12} sm={3}>
            <Controller
              control={control}
              name="fx"
              render={({ field }) => (
                <TextField
                  label="FX"
                  type="number"
                  size="small"
                  fullWidth
                  value={field.value === '' ? '' : field.value}
                  onChange={(event) =>
                    field.onChange(event.target.value === '' ? '' : Number(event.target.value))
                  }
                  onBlur={field.onBlur}
                  inputProps={{ inputMode: 'decimal' }}
                  error={!!errors.fx}
                  helperText={errors.fx?.message}
                  sx={numSx}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Controller
              control={control}
              name="quantity"
              render={({ field }) => (
                <TextField
                  label="Quantity"
                  type="number"
                  size="small"
                  fullWidth
                  value={field.value === '' ? '' : field.value}
                  onChange={(event) =>
                    field.onChange(event.target.value === '' ? '' : Number(event.target.value))
                  }
                  onBlur={field.onBlur}
                  inputProps={{ inputMode: 'decimal' }}
                  error={!!errors.quantity}
                  helperText={errors.quantity?.message}
                  sx={numSx}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Controller
              control={control}
              name="price_unit"
              render={({ field }) => {
                const sel = priceUnits.find((u) => u.value === field.value)
                  ?? (field.value ? { value: field.value, label: '' } : null)
                // inject sel vào options → tắt MUI "none of options match" (anti-pattern #46)
                const priceUnitOpts = sel && !priceUnits.some((o) => o.value === sel.value) ? [sel, ...priceUnits] : priceUnits
                return (
                  <Autocomplete
                    size="small"
                    fullWidth
                    options={priceUnitOpts}
                    value={sel}
                    onChange={(_e, v) => field.onChange(v?.value ?? '')}
                    getOptionLabel={(o) => o ? (o.value === o.label ? o.value : `${o.value} — ${o.label ?? ''}`) : ''}
                    isOptionEqualToValue={(a, b) => a.value === b.value}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Price Unit"
                        error={!!errors.price_unit}
                        helperText={errors.price_unit?.message}
                      />
                    )}
                  />
                )
              }}
            />
          </Grid>

          {/* Row 5: Amount (3) */}
          <Grid item xs={12} sm={3}>
            <Controller
              control={control}
              name="amount"
              render={({ field }) => (
                <TextField
                  label="Amount"
                  type="number"
                  size="small"
                  fullWidth
                  value={field.value === '' ? '' : field.value}
                  onChange={(event) =>
                    field.onChange(numberValueOrEmpty(event.target.value))
                  }
                  onBlur={field.onBlur}
                  inputProps={{ inputMode: 'decimal' }}
                  error={!!errors.amount}
                  helperText={
                    errors.amount?.message ??
                    'Auto: quantity × price × deposit% (or total_repayment)'
                  }
                  sx={numSx}
                />
              )}
            />
          </Grid>

          {/* Row 6: Note (12) */}
          <Grid item xs={12}>
            <Controller
              control={control}
              name="note"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Note"
                  size="small"
                  multiline
                  minRows={2}
                  fullWidth
                  error={!!errors.note}
                  helperText={errors.note?.message}
                />
              )}
            />
          </Grid>
        </Grid>

        {showDetailList ? (
          <Box sx={{ mt: 2 }}>
            <Divider sx={{ mb: 1 }} />
            <Stack spacing={1}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="subtitle2">Deposit Details</Typography>
                {detailListLoading || detailListFetching ? <CircularProgress size={16} /> : null}
              </Stack>
              {/* Usage hint — explains the editor workflow in English. */}
              <Alert severity="info" variant="outlined" sx={{ py: 0.25 }}>
                <Typography variant="caption">
                  <strong>How to use:</strong> Double-click a row below to load it into the
                  form above for editing. <strong>Save</strong> reloads the list and keeps
                  this window open so you can add another row — use <strong>Clean</strong>{' '}
                  to clear the form, or <strong>Cancel</strong> to close.
                </Typography>
              </Alert>
              {detailListError ? <Alert severity="error">Failed to load deposit details.</Alert> : null}
              {!currentContractNo ? (
                <Typography variant="body2" color="text.secondary">
                  Select a contract to load deposit details.
                </Typography>
              ) : null}
              {currentContractNo && !detailListLoading && !detailListError && detailRows.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No deposit details found.
                </Typography>
              ) : null}
              {currentContractNo && detailRows.length > 0 ? (
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320 }}>
                  <Table size="small" stickyHeader sx={{ minWidth: 2200 }}>
                    <TableHead>
                      <TableRow>
                        {DETAIL_COLUMNS.map((column) => (
                          <TableCell key={String(column.key)} sx={{ whiteSpace: 'nowrap', fontWeight: 700 }}>
                            {column.label}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detailRows.map((row) => (
                        <TableRow
                          key={row.id}
                          hover
                          selected={currentEditing?.id === row.id}
                          onDoubleClick={() => loadRowForEdit(row)}
                          title="Double-click to edit this row"
                          sx={{ cursor: 'pointer' }}
                        >
                          {DETAIL_COLUMNS.map((column) => (
                            <TableCell key={String(column.key)} sx={{ whiteSpace: 'nowrap' }}>
                              {formatDetailValue(row, column) || '-'}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : null}
            </Stack>
          </Box>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}

export default DepositFormDialog
